
import React, { useState } from 'react';
import { HashRouter, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Mountain, Search, LayoutGrid, MessageSquareText, Users, Menu, X, ChevronRight, ShieldCheck, LogOut, UserCircle, FileText, CreditCard } from 'lucide-react';
import { HomePage } from './components/HomePage';
import { DirectoryPage } from './components/DirectoryPage';
import { ConsultantDashboard } from './components/ConsultantDashboard';
import { GeminiAdvisor } from './components/GeminiAdvisor';
import { ConsultancyProfile } from './components/ConsultancyProfile';
import { AdminDashboard } from './components/AdminDashboard';
import { UserDashboard } from './components/UserDashboard';
import { PricingPage } from './components/PricingPage';
import { ResourcesPage } from './components/ResourcesPage';
import { LoginPage } from './components/auth/LoginPage';
import { SignupPage } from './components/auth/SignupPage';
import { ConsultancyOnboarding } from './components/onboarding/ConsultancyOnboarding';
import { ClientOnboarding } from './components/onboarding/ClientOnboarding';
import { RFQBuilder } from './components/client/RFQBuilder';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserRole } from './types';

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  // Hide navigation on auth/onboarding pages
  if (['/login', '/signup', '/onboarding/consultancy', '/onboarding/client'].includes(location.pathname)) {
      return null;
  }

  const publicLinks = [
    { name: 'Browse Consultancies', path: '/directory', icon: Search },
    { name: 'Pricing', path: '/pricing', icon: CreditCard },
    { name: 'Resources', path: '/resources', icon: FileText },
    { name: 'AI Advisor', path: '/advisor', icon: MessageSquareText },
  ];

  const navLinks = [...publicLinks];

  // Add dashboard links based on role
  if (user?.role === 'consultant') {
    navLinks.push({ name: 'Vendor Dashboard', path: '/dashboard', icon: LayoutGrid });
  } else if (user?.role === 'admin') {
    navLinks.push({ name: 'Admin Panel', path: '/admin', icon: ShieldCheck });
  } else if (user?.role === 'client') {
    navLinks.push({ name: 'My Dashboard', path: '/client-dashboard', icon: Users });
  }

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <Link to="/" className="flex-shrink-0 flex items-center gap-2">
              <div className="bg-blue-900 p-1.5 rounded-lg">
                <Mountain className="h-6 w-6 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">RegulatorySherpas</span>
            </Link>
          </div>
          
          <div className="hidden lg:ml-6 lg:flex lg:space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                  isActive(link.path)
                    ? 'border-blue-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <link.icon className="w-4 h-4 mr-2" />
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden lg:ml-6 lg:flex lg:items-center">
             {user ? (
               <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-600 flex items-center gap-2">
                    <UserCircle className="w-5 h-5"/> {user.name}
                  </span>
                  <button 
                    onClick={logout}
                    className="text-slate-500 hover:text-red-600 p-2"
                    title="Sign Out"
                  >
                    <LogOut className="w-5 h-5"/>
                  </button>
               </div>
             ) : (
               <div className="flex items-center gap-3">
                  <Link 
                    to="/login"
                    className="text-slate-600 hover:text-slate-900 font-medium text-sm px-3 py-2"
                  >
                    Log In
                  </Link>
                  <Link 
                    to="/signup"
                    className="bg-blue-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-800 transition-colors"
                  >
                    Sign Up
                  </Link>
               </div>
             )}
          </div>

          <div className="-mr-2 flex items-center lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden bg-white border-b border-slate-200">
          <div className="pt-2 pb-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                  isActive(link.path)
                    ? 'bg-blue-50 border-blue-900 text-blue-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <div className="flex items-center">
                  <link.icon className="w-5 h-5 mr-3" />
                  {link.name}
                </div>
              </Link>
            ))}
             {!user && (
                <div className="pt-4 pb-2 border-t border-slate-100">
                  <Link 
                    to="/login"
                    onClick={() => setIsOpen(false)}
                    className="w-full block text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-slate-600 hover:bg-gray-50"
                  >
                    Log In
                  </Link>
                  <Link 
                    to="/signup"
                    onClick={() => setIsOpen(false)}
                    className="w-full block text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-blue-600 hover:bg-gray-50"
                  >
                    Sign Up
                  </Link>
                </div>
             )}
             {user && (
                <button 
                  onClick={() => { setIsOpen(false); logout(); }}
                  className="w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-red-600 hover:bg-gray-50"
                >
                  Sign Out
                </button>
             )}
          </div>
        </div>
      )}
    </nav>
  );
};

const Footer: React.FC = () => {
  const location = useLocation();
  if (['/login', '/signup', '/onboarding/consultancy', '/onboarding/client'].includes(location.pathname)) {
      return null;
  }
  
  return (
  <footer className="bg-slate-900 text-white pt-12 pb-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="col-span-1 md:col-span-1">
          <div className="flex items-center gap-2 mb-4">
             <Mountain className="h-6 w-6 text-blue-400" />
             <span className="font-bold text-xl">RegulatorySherpas</span>
          </div>
          <p className="text-slate-400 text-sm">
            Connecting biotech innovators with the regulatory expertise needed to bring life-saving therapies to patients faster.
          </p>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-200 tracking-wider uppercase mb-4">Directory</h3>
          <ul className="space-y-2 text-slate-400 text-sm">
            <li><Link to="/directory" className="hover:text-white">Browse Consultancies</Link></li>
            <li><a href="#" className="hover:text-white">Verify Track Records</a></li>
            <li><Link to="/pricing" className="hover:text-white">Plans & Pricing</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-200 tracking-wider uppercase mb-4">Resources</h3>
          <ul className="space-y-2 text-slate-400 text-sm">
            <li><Link to="/resources" className="hover:text-white">Regulatory Guides</Link></li>
            <li><Link to="/advisor" className="hover:text-white">AI Strategy Assistant</Link></li>
            <li><a href="#" className="hover:text-white">Success Stories</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-200 tracking-wider uppercase mb-4">Contact</h3>
          <ul className="space-y-2 text-slate-400 text-sm">
            <li>support@regulatorysherpas.com</li>
            <li>+1 (555) 123-4567</li>
            <li className="pt-2">
                <span className="flex items-center gap-2 text-green-400"><ShieldCheck className="w-4 h-4"/> GDPR Compliant</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="mt-12 border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center">
        <p className="text-slate-500 text-sm">&copy; 2024 RegulatorySherpas. All rights reserved.</p>
        <div className="flex space-x-6 mt-4 md:mt-0">
          <a href="#" className="text-slate-400 hover:text-white text-sm">Privacy Policy</a>
          <a href="#" className="text-slate-400 hover:text-white text-sm">Terms of Service</a>
        </div>
      </div>
    </div>
  </footer>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode, allowedRoles: UserRole[] }> = ({ children, allowedRoles }) => {
    const { user, isLoading } = useAuth();
    
    if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-900 rounded-full border-t-transparent"></div></div>;

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (!allowedRoles.includes(user.role)) {
        return <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
           <h2 className="text-xl font-bold text-slate-900 mb-2">Permission Denied</h2>
           <p className="text-slate-500">You do not have permission to view this page.</p>
        </div>
      </div>;
    }

    return <>{children}</>;
}

const AppContent: React.FC = () => {
  return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <Navigation />
        <main className="flex-grow">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/directory" element={<DirectoryPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/consultancy/:id" element={<ConsultancyProfile />} />
            <Route path="/advisor" element={<GeminiAdvisor />} />
            
            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/onboarding/consultancy" element={<ConsultancyOnboarding />} />
            <Route path="/onboarding/client" element={<ClientOnboarding />} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={
                <ProtectedRoute allowedRoles={['consultant', 'admin']}>
                    <ConsultantDashboard />
                </ProtectedRoute>
            } />
            <Route path="/admin" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                </ProtectedRoute>
            } />
            <Route path="/client-dashboard" element={
                <ProtectedRoute allowedRoles={['client', 'admin']}>
                    <UserDashboard />
                </ProtectedRoute>
            } />
            <Route path="/client-dashboard/create-rfq" element={
                <ProtectedRoute allowedRoles={['client', 'admin']}>
                    <RFQBuilder />
                </ProtectedRoute>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <Footer />
      </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
        <HashRouter>
            <AppContent />
        </HashRouter>
    </AuthProvider>
  );
};

export default App;
